import { useFormikContext } from 'formik';
import { Dispatch, memo, SetStateAction, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { toast } from 'react-toastify';

import {
    GetGetResourcesByTypeResponse,
    KeyGetGetResourcesByType,
    ResourceType,
    useMutationDeleteDeleteResources,
} from '../../../../../backend';
import { queryClient } from '../../../../../Root';
import { errorToastConfig } from '../../../../../utils/utils';
import { useConfirmDialog } from '../../../../confirm/useConfirmDialog';
import { ResourcesPageFormValues } from '../../../../pages/ResourcesPage';
import { StyledFormBox, StyledFormButton } from './MultiResourcesForm.style';

export const MultiResourcesForm = memo(function MultiResourcesForm({
    selectedResources,
    setSelectedResources,
    arrayRemove,
    setAnchorEl,
    resourceType,
}: {
    selectedResources: number[];
    setSelectedResources: (e: number[]) => void;
    arrayRemove: (index: number) => void;
    setAnchorEl: Dispatch<SetStateAction<HTMLLabelElement>>;
    resourceType: ResourceType;
}) {
    const { values, dirty } = useFormikContext<ResourcesPageFormValues>();

    const confirm = useConfirmDialog({ disableReject: true });
    const { mutate } = useMutationDeleteDeleteResources({
        onError: () => {
            toast(<FormattedMessage id={'resourcesForm.error.deleteResource'} />, errorToastConfig);
        },
        onSuccess: (r, requestArgs) => {
            const deletedResourceNames = requestArgs.resource.map(r => r.name);
            queryClient.setQueriesData(
                {
                    queryKey: [
                        KeyGetGetResourcesByType,
                        {
                            type: resourceType,
                            includeNonActive: true,
                        },
                    ],
                },
                (values: GetGetResourcesByTypeResponse) => ({
                    ...values,
                    data: values.data.filter(r => !deletedResourceNames.includes(r.name)),
                }),
            );
            setSelectedResources([]);
        },
    });

    const handleDelete = useCallback(() => {
        setAnchorEl(null);
        const resources = values.resources.filter(r => selectedResources.includes(values.resources.indexOf(r)));
        const nbResourcesInDatabase = values.resources.filter(r => !!r.id).length;
        confirm({
            title: (
                <FormattedMessage
                    id={'resources.actions.delete.several'}
                    values={{
                        names: resources.map(r => r.name).toString(),
                        type: <FormattedMessage id={'form.value.type.' + resourceType} />,
                    }}
                />
            ),
            content:
                selectedResources.filter(r => r < nbResourcesInDatabase).length > 0 && dirty ? (
                    <FormattedMessage id={'resources.alert.delete'} />
                ) : null,
            confirmationText: <FormattedMessage id={'confirm.default.delete'} />,
            cancellationText: <FormattedMessage id={'confirm.default.cancel'} />,
        }).then(() => {
            const resourcesToDelete = resources.filter(r => !!r.id);
            if (resourcesToDelete.length > 0) {
                mutate({ resource: resourcesToDelete });
            } else {
                selectedResources.sort().forEach((index, i) => arrayRemove(index - i));
                setSelectedResources([]);
            }
        });
    }, [
        arrayRemove,
        confirm,
        dirty,
        mutate,
        resourceType,
        selectedResources,
        setAnchorEl,
        setSelectedResources,
        values.resources,
    ]);

    return (
        <StyledFormBox>
            <StyledFormButton onClick={handleDelete}>
                <FormattedMessage
                    id={`Resource.delete.resources`}
                    values={{ type: <FormattedMessage id={'form.value.type.' + resourceType} /> }}
                />
            </StyledFormButton>
        </StyledFormBox>
    );
});
